import { createFileRoute, type SearchSchemaInput } from '@tanstack/react-router';
import { operatorMeQuery } from '@/services/operator';
import { performanceQuery } from '@/services/performance';
import { roleIncludes } from '@/features/team/role-gate';
import { mayBeOperator } from '@/features/operator/operator-view';
import { PersonaPage } from '@/features/operator/persona-page';
import { OperatorPerformancePage } from '@/features/operator-performance/operator-performance-page';
import type { PerformanceAddress, PerformanceSearch } from '@/features/operator-performance/performance-search';
import { PerformancePage } from '@/features/performance/performance-page';

export const Route = createFileRoute('/_authed/performance')({
  // `tab` and `program`: the tab and campaign the operator's page shows (Back from a door lands on them). Her page
  // ignores them. Both optional, so a link without them opens the page as before. Links give a `PerformanceSearch`; the
  // address is passed on unread and his page reads it (`parsePerformanceSearch`, in its own chunk): a route's validator
  // ships in the entry chunk, which the sign-in page loads (its 150 KB budget).
  validateSearch: (search: PerformanceSearch & SearchSchemaInput): PerformanceAddress => search,
  // Her role decides the page and its money; an API without roles answers 404 (nothing narrows). Her role first: a role
  // without the Performance page shows the role card and requests nothing. The loader never throws.
  // A seat that may run the campaigns also asks whether it does (a client seat never asks), so neither page flashes.
  loader: async ({ context: { queryClient, team } }) => {
    if (!team || !(await roleIncludes(queryClient, team, 'performance'))) return;
    await Promise.all([
      queryClient.ensureQueryData(performanceQuery(team.id)).catch(() => null),
      mayBeOperator(team.role) ? queryClient.ensureQueryData(operatorMeQuery(team.id)).catch(() => null) : null,
    ]);
  },
  component: PerformanceRoute,
});

/** The operator's Performance for someone who runs the campaigns; the client's page, exactly, for everyone else. */
function PerformanceRoute() {
  return <PersonaPage operator={OperatorPerformance} managed={PerformancePage} />;
}

function OperatorPerformance() {
  return <OperatorPerformancePage search={Route.useSearch()} />;
}
